import { useCallback, useEffect, useMemo, useRef, useState } from "react";

let pdfjsPromise = null;
export function loadPdfJs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist/build/pdf")
      .then(mod => {
        const lib = mod.GlobalWorkerOptions ? mod : mod.default;
        lib.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.js", import.meta.url).toString();
        return lib;
      })
      .catch(e => { pdfjsPromise = null; throw e; });
  }
  return pdfjsPromise;
}

const SPIN_CSS = "@keyframes kpdfSpin{to{transform:rotate(360deg)}}";

function PageFace({ n, src, bg }) {
  if (!n) return null;
  return <div style={{ position: "absolute", inset: 0, background: bg, overflow: "hidden" }}>
    {src
      ? <img src={src} alt={`Page ${n}`} draggable={false} style={{ width: "100%", height: "100%", display: "block", pointerEvents: "none", userSelect: "none" }} />
      : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#cbd5e1", fontSize: 14, fontFamily: "Inter, sans-serif" }}>{n}</div>}
  </div>;
}

export function PdfFlipViewer({ url, node, interactive = true }) {
  const s = node.styles || {};
  const W = s.width || 600;
  const H = s.height || 400;
  const pageBg = node.pdfPageBg || "#ffffff";
  const uiColor = node.pdfControlsColor || "#ffffff";
  const uiBg = node.pdfControlsBg || "rgba(17,24,39,0.72)";
  const pad = node.pdfPadding ?? 24;
  const flipMs = node.pdfFlipDuration ?? 650;
  const coverAlone = node.pdfCoverAlone !== false;
  const allowToggle = node.pdfAllowModeToggle !== false;
  const initialMode = node.pdfViewMode === "double" || node.pdfViewMode === "single" ? node.pdfViewMode : (W > H * 1.15 ? "double" : "single");

  const [doc, setDoc] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [aspect, setAspect] = useState(0.707);
  const [numPages, setNumPages] = useState(0);
  const [mode, setMode] = useState(initialMode);
  const [anchor, setAnchor] = useState(1);
  const [flip, setFlipState] = useState(null);
  const [showUi, setShowUi] = useState(true);
  const [, bump] = useState(0);

  const cache = useRef(new Map());
  const pending = useRef(new Set());
  const docRef = useRef(null);
  const flipRef = useRef(null);
  const animRef = useRef(0);
  const animTimer = useRef(null);
  const animating = useRef(false);
  const gesture = useRef(null);
  const rootRef = useRef(null);
  const uiTimer = useRef(null);

  const setFlip = v => { flipRef.current = v; setFlipState(v); };

  useEffect(() => { setMode(initialMode); }, [initialMode]);

  useEffect(() => {
    let cancelled = false;
    let task = null;
    cache.current.forEach(u => URL.revokeObjectURL(u));
    cache.current = new Map();
    pending.current = new Set();
    docRef.current = null;
    setDoc(null);
    setNumPages(0);
    setAnchor(1);
    setFlip(null);
    if (!url) { setStatus("idle"); return undefined; }
    setStatus("loading");
    loadPdfJs()
      .then(lib => {
        if (cancelled) return null;
        task = lib.getDocument({ url });
        return task.promise;
      })
      .then(async d => {
        if (!d || cancelled) return;
        const first = await d.getPage(1);
        const vp = first.getViewport({ scale: 1 });
        if (cancelled) return;
        docRef.current = d;
        setAspect(vp.width / vp.height);
        setNumPages(d.numPages);
        setDoc(d);
        setStatus("ready");
      })
      .catch(e => {
        if (cancelled) return;
        setStatus("error");
        setError(e?.message || "Could not load this PDF");
      });
    return () => {
      cancelled = true;
      try { task && task.destroy(); } catch (_) {}
    };
  }, [url]);

  useEffect(() => () => {
    cancelAnimationFrame(animRef.current);
    clearTimeout(animTimer.current);
    clearTimeout(uiTimer.current);
    cache.current.forEach(u => URL.revokeObjectURL(u));
  }, []);

  const double = mode === "double";
  const availW = Math.max(10, W - pad * 2);
  const availH = Math.max(10, H - pad * 2);
  const pageH = Math.max(10, Math.min(availH, (double ? availW / 2 : availW) / aspect));
  const pageW = pageH * aspect;
  const bookW = double ? pageW * 2 : pageW;
  const bookLeft = (W - bookW) / 2;
  const bookTop = (H - pageH) / 2;

  const spreads = useMemo(() => {
    const out = [];
    if (!numPages) return out;
    if (!double) {
      for (let i = 1; i <= numPages; i++) out.push([i]);
      return out;
    }
    let i = 1;
    if (coverAlone) { out.push([null, 1]); i = 2; }
    for (; i <= numPages; i += 2) out.push([i, i + 1 <= numPages ? i + 1 : null]);
    return out;
  }, [numPages, double, coverAlone]);

  const sIdx = Math.max(0, spreads.findIndex(sp => sp.includes(anchor)));
  const cur = spreads[sIdx] || [];
  const next = spreads[sIdx + 1] || null;
  const prev = sIdx > 0 ? spreads[sIdx - 1] : null;

  const renderH = Math.min(2600, Math.round(availH * (node.pdfQuality || 1.6)));
  const ensure = useCallback(async n => {
    const d = docRef.current;
    if (!d || !n || n > d.numPages || cache.current.has(n) || pending.current.has(n)) return;
    pending.current.add(n);
    try {
      const page = await d.getPage(n);
      const base = page.getViewport({ scale: 1 });
      const vp = page.getViewport({ scale: renderH / base.height });
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(vp.width);
      canvas.height = Math.ceil(vp.height);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport: vp }).promise;
      const blob = await new Promise(r => canvas.toBlob(r, "image/jpeg", 0.9));
      if (blob && docRef.current === d) {
        cache.current.set(n, URL.createObjectURL(blob));
        bump(x => x + 1);
      }
    } catch (_) {
    } finally {
      pending.current.delete(n);
    }
  }, [renderH]);

  const wantKey = [...cur, ...(next || []), ...(prev || []), ...(spreads[sIdx + 2] || [])].filter(Boolean).join(",");
  useEffect(() => {
    if (!doc || !wantKey) return;
    const want = wantKey.split(",").map(Number);
    let chain = Promise.resolve();
    want.forEach(n => { chain = chain.then(() => ensure(n)); });
    if (cache.current.size > 24) {
      cache.current.forEach((u, n) => {
        if (Math.abs(n - want[0]) > 8) { URL.revokeObjectURL(u); cache.current.delete(n); }
      });
    }
  }, [doc, wantKey, ensure]);

  const pokeUi = useCallback(() => {
    setShowUi(true);
    clearTimeout(uiTimer.current);
    const secs = node.pdfControlsAutoHide ?? 4;
    if (secs > 0 && interactive) uiTimer.current = setTimeout(() => setShowUi(false), secs * 1000);
  }, [node.pdfControlsAutoHide, interactive]);

  useEffect(() => { pokeUi(); }, [pokeUi]);

  const animateTo = (dir, from, to, done) => {
    cancelAnimationFrame(animRef.current);
    animating.current = true;
    clearTimeout(animTimer.current);
    const dur = flipMs * Math.max(0.15, Math.abs(to - from));
    const t0 = performance.now();
    const finish = () => {
      if (!animating.current) return;
      cancelAnimationFrame(animRef.current);
      clearTimeout(animTimer.current);
      animating.current = false;
      done();
    };
    const tick = now => {
      const k = dur <= 0 ? 1 : Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - k, 3);
      setFlip({ dir, p: from + (to - from) * e });
      if (k < 1) animRef.current = requestAnimationFrame(tick);
      else finish();
    };
    animRef.current = requestAnimationFrame(tick);
    animTimer.current = setTimeout(finish, dur + 150);
  };

  const commit = dir => {
    const target = dir === "next" ? next : prev;
    if (target) setAnchor(target.find(Boolean));
    setFlip(null);
  };

  const go = dir => {
    if (animating.current || flipRef.current) return;
    if (dir === "next" ? !next : !prev) return;
    animateTo(dir, 0, 1, () => commit(dir));
  };

  const goRef = useRef(go);
  goRef.current = go;
  useEffect(() => {
    if (!interactive) return undefined;
    const onKey = e => {
      if (e.key === "ArrowRight") goRef.current("next");
      else if (e.key === "ArrowLeft") goRef.current("prev");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [interactive]);

  const onDown = e => {
    if (!interactive || status !== "ready" || animating.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    gesture.current = { x: e.clientX, y: e.clientY, t: performance.now(), lastX: e.clientX, lastT: performance.now(), v: 0, scale: rect.width / W || 1, left: rect.left, dir: null };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };

  const onMove = e => {
    const g = gesture.current;
    if (!g) return;
    const dx = (e.clientX - g.x) / g.scale;
    const now = performance.now();
    g.v = ((e.clientX - g.lastX) / g.scale) / Math.max(1, now - g.lastT) * 1000;
    g.lastX = e.clientX;
    g.lastT = now;
    if (!g.dir && Math.abs(dx) > 10) {
      const dir = dx < 0 ? "next" : "prev";
      g.dir = (dir === "next" ? next : prev) ? dir : "none";
    }
    if (g.dir && g.dir !== "none") {
      const dist = double ? pageW * 1.2 : pageW * 0.9;
      const raw = g.dir === "next" ? -dx : dx;
      setFlip({ dir: g.dir, p: Math.max(0, Math.min(1, raw / dist)) });
    }
  };

  const onUp = e => {
    const g = gesture.current;
    if (!g) return;
    gesture.current = null;
    if (!g.dir) {
      const moved = Math.hypot(e.clientX - g.x, e.clientY - g.y) / g.scale;
      if (moved > 12) return;
      const localX = (e.clientX - g.left) / g.scale;
      const zone = node.pdfTapZone ?? 0.3;
      if (localX < W * zone) go("prev");
      else if (localX > W * (1 - zone)) go("next");
      else if (showUi) { clearTimeout(uiTimer.current); setShowUi(false); }
      else pokeUi();
      return;
    }
    if (g.dir === "none") return;
    const f = flipRef.current;
    const p = f ? f.p : 0;
    const fling = g.dir === "next" ? g.v < -600 : g.v > 600;
    const done = p > 0.3 || (fling && p > 0.05);
    animateTo(g.dir, p, done ? 1 : 0, done ? () => commit(g.dir) : () => setFlip(null));
  };

  const img = n => (n ? cache.current.get(n) : null);
  const persp = Math.max(1200, pageW * 4);
  const shade = f => (f ? Math.sin(Math.PI * f.p) : 0);

  const rootStyle = { width: "100%", height: "100%", position: "relative", overflow: "hidden", touchAction: "none", userSelect: "none", WebkitUserSelect: "none", pointerEvents: interactive ? "auto" : "none", fontFamily: s.fontFamily || "Inter, sans-serif" };

  if (status !== "ready") {
    return <div style={{ ...rootStyle, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, color: s.color || "#64748b", fontSize: 13, textAlign: "center", padding: 16, boxSizing: "border-box" }}>
      <style>{SPIN_CSS}</style>
      {status === "loading" && <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid rgba(100,116,139,0.25)", borderTopColor: "#64748b", animation: "kpdfSpin 0.8s linear infinite" }} />}
      {status === "loading" && <span>{node.pdfLoadingText || "Opening document…"}</span>}
      {status === "idle" && <><span style={{ fontSize: 34 }}>📖</span><span>{node.pdfEmptyText || "No document selected"}</span></>}
      {status === "error" && <><span style={{ fontSize: 30 }}>⚠</span><span>{error}</span><span style={{ fontSize: 11, opacity: 0.7 }}>Check the URL and that the server allows cross-origin (CORS) requests.</span></>}
    </div>;
  }

  const f = flip;
  let staticLeft = null;
  let staticRight = null;
  let sheet = null;

  if (double) {
    staticLeft = cur[0];
    staticRight = cur[1];
    if (f && f.dir === "next" && next) {
      staticRight = next[1];
      sheet = { left: pageW, origin: "left center", angle: -180 * f.p, front: cur[1], back: next[0] };
    } else if (f && f.dir === "prev" && prev) {
      staticLeft = prev[0];
      sheet = { left: 0, origin: "right center", angle: 180 * f.p, front: cur[0], back: prev[1] };
    }
  } else {
    staticLeft = cur[0];
    if (f && f.dir === "next" && next) {
      staticLeft = next[0];
      sheet = { left: 0, origin: "left center", angle: 92 * f.p, front: cur[0], back: null };
    } else if (f && f.dir === "prev" && prev) {
      sheet = { left: 0, origin: "left center", angle: 92 * (1 - f.p), front: prev[0], back: null };
    }
  }

  const pageShadow = "0 10px 30px rgba(0,0,0,0.28)";
  const label = double
    ? cur.filter(Boolean).join("–")
    : String(cur[0] || "");
  const k = shade(f);

  return <div ref={rootRef} style={rootStyle} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
    <div style={{ position: "absolute", left: bookLeft, top: bookTop, width: bookW, height: pageH, perspective: persp }}>
      {double
        ? <>
            {staticLeft && <div style={{ position: "absolute", left: 0, top: 0, width: pageW, height: pageH, boxShadow: pageShadow }}>
              <PageFace n={staticLeft} src={img(staticLeft)} bg={pageBg} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to left, rgba(0,0,0,0.18), rgba(0,0,0,0) 12%)", pointerEvents: "none" }} />
            </div>}
            {staticRight && <div style={{ position: "absolute", left: pageW, top: 0, width: pageW, height: pageH, boxShadow: pageShadow }}>
              <PageFace n={staticRight} src={img(staticRight)} bg={pageBg} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(0,0,0,0.18), rgba(0,0,0,0) 12%)", pointerEvents: "none" }} />
            </div>}
          </>
        : staticLeft && <div style={{ position: "absolute", left: 0, top: 0, width: pageW, height: pageH, boxShadow: pageShadow }}>
            <PageFace n={staticLeft} src={img(staticLeft)} bg={pageBg} />
          </div>}

      {sheet && <div style={{ position: "absolute", left: sheet.left, top: 0, width: pageW, height: pageH, transformOrigin: sheet.origin, transform: `rotateY(${sheet.angle}deg)`, transformStyle: "preserve-3d", zIndex: 5 }}>
        <div style={{ position: "absolute", inset: 0, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", boxShadow: `0 ${8 + 14 * k}px ${24 + 30 * k}px rgba(0,0,0,${0.25 + 0.2 * k})` }}>
          {sheet.front ? <PageFace n={sheet.front} src={img(sheet.front)} bg={pageBg} /> : <div style={{ position: "absolute", inset: 0, background: pageBg }} />}
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: sheet.origin.startsWith("left") ? `linear-gradient(to left, rgba(0,0,0,${0.3 * k}), rgba(255,255,255,${0.12 * k}) 60%, rgba(0,0,0,0))` : `linear-gradient(to right, rgba(0,0,0,${0.3 * k}), rgba(255,255,255,${0.12 * k}) 60%, rgba(0,0,0,0))` }} />
        </div>
        {double && <div style={{ position: "absolute", inset: 0, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)", boxShadow: pageShadow }}>
          {sheet.back ? <PageFace n={sheet.back} src={img(sheet.back)} bg={pageBg} /> : <div style={{ position: "absolute", inset: 0, background: pageBg }} />}
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `linear-gradient(to ${sheet.origin.startsWith("left") ? "left" : "right"}, rgba(0,0,0,${0.25 * k}), rgba(0,0,0,0) 70%)` }} />
        </div>}
      </div>}

      {f && <div style={{ position: "absolute", top: 0, height: pageH, pointerEvents: "none", zIndex: 4, opacity: k * 0.6,
        left: double ? (f.dir === "next" ? pageW : 0) : 0, width: pageW,
        background: double && f.dir === "prev" ? "linear-gradient(to left, rgba(0,0,0,0.35), rgba(0,0,0,0) 40%)" : "linear-gradient(to right, rgba(0,0,0,0.35), rgba(0,0,0,0) 40%)" }} />}
    </div>

    {node.pdfShowControls !== false && <div onPointerDown={e => e.stopPropagation()} onPointerUp={e => e.stopPropagation()}
      style={{ position: "absolute", left: "50%", bottom: Math.max(8, pad / 2), transform: `translateX(-50%) translateY(${showUi ? 0 : 16}px)`, opacity: showUi ? 1 : 0, pointerEvents: showUi && interactive ? "auto" : "none", transition: "opacity 0.25s, transform 0.25s", display: "flex", alignItems: "center", gap: 4, background: uiBg, color: uiColor, borderRadius: 999, padding: "4px 6px", fontSize: 13, backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", boxShadow: "0 4px 18px rgba(0,0,0,0.25)", zIndex: 10, whiteSpace: "nowrap" }}>
      <UiBtn color={uiColor} disabled={!prev} onClick={() => { go("prev"); pokeUi(); }}>‹</UiBtn>
      <span style={{ padding: "0 8px", fontVariantNumeric: "tabular-nums", opacity: 0.9 }}>{label} / {numPages}</span>
      <UiBtn color={uiColor} disabled={!next} onClick={() => { go("next"); pokeUi(); }}>›</UiBtn>
      {allowToggle && <>
        <span style={{ width: 1, height: 18, background: uiColor, opacity: 0.25, margin: "0 4px" }} />
        <UiBtn color={uiColor} onClick={() => { setMode(m => (m === "double" ? "single" : "double")); pokeUi(); }} title={double ? "Single page" : "Two pages"}>
          <span style={{ display: "inline-flex", gap: 2 }}>
            <span style={{ width: 9, height: 13, border: `1.5px solid ${uiColor}`, borderRadius: 1.5 }} />
            {!double && <span style={{ width: 9, height: 13, border: `1.5px solid ${uiColor}`, borderRadius: 1.5 }} />}
          </span>
        </UiBtn>
      </>}
    </div>}
  </div>;
}

function UiBtn({ children, onClick, disabled, color, title }) {
  return <button type="button" title={title} disabled={disabled} onClick={onClick}
    style={{ background: "transparent", border: "none", color, opacity: disabled ? 0.3 : 1, cursor: disabled ? "default" : "pointer", fontSize: 20, lineHeight: 1, minWidth: 34, height: 30, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
    {children}
  </button>;
}
